import type { Provider } from "@supabase/supabase-js";
import { env } from "./env";
import { supabase } from "./supabase";

/** The social providers the app knows how to present. */
export type SocialProvider = Extract<Provider, "google" | "apple">;
export const SOCIAL_PROVIDERS: SocialProvider[] = ["google", "apple"];

export const PROVIDER_LABEL: Record<SocialProvider, string> = {
  google: "Google",
  apple: "Apple",
};

const RETURN_KEY = "soundlayer:auth:return";
const LINKING_KEY = "soundlayer:auth:linking";

/**
 * Which social providers the Supabase project has switched on. Read from
 * the project's public settings endpoint at runtime, so a provider whose
 * dashboard configuration is incomplete never shows a button. On any
 * failure the answer is "none": email and password still work.
 */
export async function enabledProviders(): Promise<Set<SocialProvider>> {
  if (!env.supabaseUrl || !env.supabaseKey) return new Set();
  try {
    const response = await fetch(`${env.supabaseUrl}/auth/v1/settings`, {
      headers: { apikey: env.supabaseKey },
    });
    if (!response.ok) return new Set();
    const body = (await response.json()) as { external?: Record<string, boolean> };
    return new Set(SOCIAL_PROVIDERS.filter((p) => body.external?.[p] === true));
  } catch {
    return new Set();
  }
}

export function callbackUrl(): string {
  return `${window.location.origin}/auth/callback`;
}

export function rememberReturn(path: string) {
  try {
    sessionStorage.setItem(RETURN_KEY, path);
  } catch {
    /* no storage: fall back to home after sign-in */
  }
}

export function takeReturn(): string | null {
  try {
    const value = sessionStorage.getItem(RETURN_KEY);
    sessionStorage.removeItem(RETURN_KEY);
    return value && value.startsWith("/") ? value : null;
  } catch {
    return null;
  }
}

export function markLinking(provider: SocialProvider) {
  try {
    sessionStorage.setItem(LINKING_KEY, provider);
  } catch {
    /* ignore */
  }
}

export function takeLinking(): SocialProvider | null {
  try {
    const value = sessionStorage.getItem(LINKING_KEY) as SocialProvider | null;
    sessionStorage.removeItem(LINKING_KEY);
    return value;
  } catch {
    return null;
  }
}

/**
 * Forget an abandoned "connect a provider" attempt. Called whenever an
 * ordinary sign-in starts, so a stale marker can never make a later
 * sign-in claim that a provider was connected.
 */
export function clearLinking(): void {
  takeLinking();
}

/** Start a sign-in with a social provider. Navigates away on success. */
export async function signInWith(provider: SocialProvider, returnTo: string): Promise<string | null> {
  if (!supabase) return "Sign-in is not configured.";
  clearLinking();
  rememberReturn(returnTo);
  const { error } = await supabase.auth.signInWithOAuth({
    provider,
    options: {
      redirectTo: callbackUrl(),
      queryParams: provider === "google" ? { prompt: "select_account" } : undefined,
    },
  });
  return error ? error.message : null;
}

/**
 * Add a provider to the signed-in user's SoundLayer ID (Supabase identity
 * linking). The current user is the only account touched: nothing is
 * merged and no library moves. Requires "manual linking" in the project's
 * Auth settings; Supabase reports an error otherwise.
 */
export async function linkProvider(provider: SocialProvider): Promise<string | null> {
  if (!supabase) return "Sign-in is not configured.";
  markLinking(provider);
  rememberReturn("/account");
  const { error } = await supabase.auth.linkIdentity({
    provider,
    options: { redirectTo: callbackUrl() },
  });
  if (error) takeLinking();
  return error ? error.message : null;
}

export type Audience = "client" | "dealer";

/** Where a password reset link returns. Same origin as the request; the audience only picks the home screen afterwards. */
export function recoveryReturnUrl(audience: Audience): string {
  return `${window.location.origin}/auth/recover${audience === "dealer" ? "?audience=dealer" : ""}`;
}

/**
 * Ask Supabase to email a password reset link. Supabase does not reveal
 * whether the address has an account, and neither does the app: the
 * caller shows the same neutral message on success. Only a transport
 * or rate-limit error is surfaced.
 */
export async function requestPasswordReset(email: string, audience: Audience): Promise<string | null> {
  if (!supabase) return "Sign-in is not configured.";
  clearLinking();
  const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), { redirectTo: recoveryReturnUrl(audience) });
  return error ? error.message : null;
}

export type LinkKind = "confirmation" | "reset";

/** Error parameters Supabase may append to a redirect, from the query or the hash. */
export function authErrorFrom(search: string, hash: string, kind: LinkKind = "confirmation"): { code: string | null; message: string } | null {
  for (const raw of [search.replace(/^\?/, ""), hash.replace(/^#/, "")]) {
    const params = new URLSearchParams(raw);
    const message = describeAuthError(params, kind);
    if (message) return { code: params.get("error_code") ?? params.get("error"), message };
  }
  return null;
}

/** Turn the provider's error parameters into one sentence for the user. */
export function describeAuthError(params: URLSearchParams, kind: LinkKind = "confirmation"): string | null {
  const code = params.get("error_code") ?? params.get("error");
  const description = params.get("error_description");
  if (!code && !description) return null;
  if (code === "access_denied" || code === "user_cancelled_authorize" || /cancel/i.test(description ?? "")) {
    return "Sign-in was cancelled. Nothing changed.";
  }
  if (code === "identity_already_exists") {
    return "That account is already connected to a different SoundLayer ID. Sign in to that ID instead; libraries are never merged automatically.";
  }
  if (code === "manual_linking_disabled") {
    return "Connecting sign-in methods is not available yet.";
  }
  if (code === "otp_expired" || /expired|invalid/i.test(description ?? "")) {
    return kind === "reset"
      ? "This reset link is invalid, has expired or was already used."
      : "This confirmation link is invalid or has expired.";
  }
  return description?.replace(/\+/g, " ") ?? `Sign-in failed (${code}).`;
}
